<!--
  Shopify onboarding "Learning Mode" view.
  Shown while Behamics is still learning the store. Once learning completes and
  the (upcoming) endpoint reports learningMode = false, this whole block is
  hidden and the live dashboard takes over.
-->
<div class="learning-view" *ngIf="learningMode()">
  <!-- Hero banner -->
  <section class="learning-hero">
    <div class="learning-hero__main">
      <span class="learning-hero__badge">
        <span class="learning-hero__badge-dot"></span>
        Learning mode
      </span>

      <h1 class="learning-hero__title">Behamics is learning your store</h1>

      <p class="learning-hero__desc">
        We're tracking visits and mapping how your shoppers browse, hesitate and
        convert. As soon as the models understand your store's patterns, nudges
        and signals switch on automatically. No setup required from your end,
        but you stay in control of whether to activate or deactivate the nudges.
      </p>

      <div class="learning-hero__progress">
        <div class="learning-hero__progress-labels">
          <span>{{ learningStatus.label }}</span>
          <span>{{ learningStatus.estimatedTimeLeftLabel }}</span>
        </div>
        <div class="learning-hero__progress-track">
          <div
            class="learning-hero__progress-bar"
            [style.width.%]="learningStatus.percentage"
          ></div>
        </div>
      </div>
    </div>

    <aside class="learning-hero__aside">
      <div class="learning-hero__aside-head">
        <span class="learning-hero__aside-icon">
          <mat-icon>bolt</mat-icon>
        </span>
        <p>Auto-activate<br />when ready</p>
      </div>

      <div class="learning-hero__toggle">
        <span
          class="learning-hero__toggle-state"
          [class.is-on]="autoActivate()"
        >
          <span class="learning-hero__toggle-dot"></span>
          {{ autoActivate() ? "On" : "Off" }}
        </span>
        <mat-slide-toggle
          [checked]="autoActivate()"
          (change)="onAutoActivateChange($event.checked)"
        ></mat-slide-toggle>
      </div>

      <p class="learning-hero__aside-note">
        We'll switch nudges &amp; signals on the moment learning completes.
      </p>
    </aside>
  </section>

  <!-- Explore Behamics -->
  <section class="learning-explore">
    <h2 class="learning-explore__title">
      While we learn, get to know Behamics
    </h2>
    <p class="learning-explore__subtitle">
      Explore how Behamics improves the customer experience and lifts store
      performance.
    </p>

    <div
      class="learning-explore__grid"
      [class.learning-explore__grid--two]="exploreCards().length === 2"
    >
      <button
        *ngFor="let card of exploreCards()"
        type="button"
        class="explore-card"
        [ngClass]="'explore-card--' + card.theme"
        (click)="onExploreCard(card.key)"
      >
        <span class="explore-card__icon">
          <!-- play -->
          <svg
            *ngIf="card.icon === 'play'"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
          <!-- doc -->
          <svg
            *ngIf="card.icon === 'doc'"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linejoin="round"
            />
            <path
              d="M8.5 11h7M8.5 14.5h7M8.5 8h3"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
          <!-- search -->
          <svg
            *ngIf="card.icon === 'search'"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="6"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <path
              d="m20 20-3.2-3.2"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </span>

        <div class="explore-card__text">
          <span class="explore-card__title">{{ card.title }}</span>
          <span class="explore-card__subtitle">{{ card.subtitle }}</span>
        </div>
      </button>
    </div>
  </section>

  <!-- Locked previews -->
  <section class="learning-locked">
    <!-- Nudge -->
    <div class="locked-panel">
      <div class="locked-panel__head">
        <div class="locked-panel__title">
          <span class="locked-panel__avatar locked-panel__avatar--nudge">
            <img src="../../assets/img/nudge_icon.svg" alt="Nudge" />
          </span>
          <div>
            <span class="locked-panel__name">Nudge</span>
            <span class="locked-panel__meta">CR &amp; RPV vs. control</span>
          </div>
        </div>
        <span class="locked-panel__tag">Not live yet</span>
      </div>

      <div class="locked-panel__body">
        <!-- Placeholder bars, blurred behind the lock -->
        <div class="locked-panel__chart" aria-hidden="true">
          <span style="height: 40%"></span>
          <span style="height: 70%"></span>
          <span style="height: 55%"></span>
          <span style="height: 85%"></span>
          <span style="height: 45%"></span>
        </div>

        <div class="locked-panel__overlay">
          <span class="locked-panel__lock">
            <mat-icon>lock</mat-icon>
          </span>
          <p class="locked-panel__overlay-title">Nudges aren't live yet</p>
          <p class="locked-panel__overlay-note">
            Uplift charts appear here once Learning Mode completes.
          </p>
        </div>
      </div>
    </div>

    <!-- Signals -->
    <div class="locked-panel">
      <div class="locked-panel__head">
        <div class="locked-panel__title">
          <span class="locked-panel__avatar locked-panel__avatar--signals">
            <img src="../../assets/img/signals_icon.svg" alt="Signals" />
          </span>
          <div>
            <span class="locked-panel__name">Signals</span>
            <span class="locked-panel__meta">Behaviour trends over time</span>
          </div>
        </div>
        <span class="locked-panel__tag">Not live yet</span>
      </div>

      <div class="locked-panel__body">
        <!-- Placeholder rows, blurred behind the lock -->
        <div class="locked-panel__table" aria-hidden="true">
          <div class="locked-panel__row locked-panel__row--head">
            <span>Score</span>
            <span>User</span>
            <span>Pages</span>
            <span>Duration</span>
          </div>
          <div class="locked-panel__row" *ngFor="let r of [1, 2, 3, 4]">
            <span>View</span>
            <span>xxxxxxxx</span>
            <span>1</span>
            <span>0:00 m</span>
          </div>
        </div>

        <div class="locked-panel__overlay">
          <span class="locked-panel__lock">
            <mat-icon>lock</mat-icon>
          </span>
          <p class="locked-panel__overlay-title">Signals aren't live yet</p>
          <p class="locked-panel__overlay-note">
            Behaviour signals appear here once Learning Mode completes.
          </p>
        </div>
      </div>
    </div>
  </section>
</div>

<!--
  Shopify LIVE view.
  Shown once the learning phase is over (learningMode = false). Behamics is
  active: the Nudge and Signals panels now show real data.
-->
<div class="learning-view" *ngIf="!learningMode()">
  <!-- LIVE banner -->
  <section class="live-banner">
    <div class="live-banner__main">
      <span class="live-banner__badge" [class.offline]="behamicsDeactivated()">
        <span class="live-banner__badge-dot"></span>
        {{ behamicsDeactivated() ? "Offline" : "Live" }}
      </span>
      <p class="live-banner__text">
        <strong>Behamics is active and running.</strong>
        <span>Nudges and signals are live on your store.</span>
      </p>
    </div>

    <div class="live-banner__toggle">
      <span class="live-banner__toggle-label"
        >Behamics Nudge & Signals Enabled</span
      >
      <mat-slide-toggle
        [checked]="!behamicsDeactivated()"
        (change)="onBehamicsToggle($event)"
      ></mat-slide-toggle>
    </div>
  </section>

  <!-- Explore Behamics -->
  <section class="learning-explore learning-explore--live">
    <h2 class="learning-explore__title">
      See how Behamics improves your store
    </h2>

    <div
      class="learning-explore__grid"
      [class.learning-explore__grid--two]="exploreCards().length === 2"
    >
      <button
        *ngFor="let card of exploreCards()"
        type="button"
        class="explore-card"
        [ngClass]="'explore-card--' + card.theme"
        (click)="onExploreCard(card.key)"
      >
        <span class="explore-card__icon">
          <svg
            *ngIf="card.icon === 'play'"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
          <svg
            *ngIf="card.icon === 'doc'"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linejoin="round"
            />
            <path
              d="M8.5 11h7M8.5 14.5h7M8.5 8h3"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
          <svg
            *ngIf="card.icon === 'search'"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="6"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <path
              d="m20 20-3.2-3.2"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </span>

        <div class="explore-card__text">
          <span class="explore-card__title">{{ card.title }}</span>
          <span class="explore-card__subtitle">{{ card.subtitle }}</span>
        </div>
      </button>
    </div>
  </section>

  <!-- Live panels -->
  <section class="learning-locked">
    <!-- Nudge -->
    <div class="locked-panel">
      <div class="locked-panel__head">
        <div class="locked-panel__title">
          <span class="locked-panel__avatar locked-panel__avatar--nudge">
            <img src="../../assets/img/nudge_icon.svg" alt="Nudge" />
          </span>
          <div>
            <span class="locked-panel__name">Nudge</span>
            <span class="locked-panel__meta">{{
              abTestingView()
                ? "Top winning experiments"
                : "CR & RPV vs. control"
            }}</span>
          </div>
        </div>
        <span
          class="locked-panel__tag locked-panel__tag--live"
          [class.offline]="behamicsDeactivated()"
        >
          <span class="locked-panel__tag-dot"></span>
          {{ behamicsDeactivated() ? "Offline" : "Live" }}
        </span>
      </div>

      <div class="live-panel__body">
        <app-shopify-nudge-card></app-shopify-nudge-card>
      </div>
    </div>

    <!-- Signals -->
    <div class="locked-panel">
      <div class="locked-panel__head">
        <div class="locked-panel__title">
          <span class="locked-panel__avatar locked-panel__avatar--signals">
            <img src="../../assets/img/signals_icon.svg" alt="Signals" />
          </span>
          <div>
            <span class="locked-panel__name">Signals</span>
            <span class="locked-panel__meta">Traffic &amp; value flow</span>
          </div>
        </div>
        <span
          class="locked-panel__tag locked-panel__tag--live"
          [class.offline]="behamicsDeactivated()"
        >
          <span class="locked-panel__tag-dot"></span>
          {{ behamicsDeactivated() ? "Offline" : "Live" }}
        </span>
      </div>

      <div class="live-panel__body">
        <app-shopify-signals-table></app-shopify-signals-table>
      </div>
    </div>
  </section>
</div>
